import {
  createEffect,
  createMemo,
  createSignal,
  onMount,
  onCleanup,
  For,
  Show,
  Index
} from "solid-js";
import { View, hexColor, Text } from "@lightningtv/solid";
import { Column, Row } from "@lightningtv/solid-ui";
import { LazyUp } from "@lightningtv/solid/primitives";
import styles from "../../styles";
import gsap from "gsap";

import { useNavigate, useParams } from "@solidjs/router";
import moviesProvider from "../../api/providers/movies";
import { createInfiniteScroll } from "../../components/pagination";
import moviesCategory from "../../api/providers/moviesCategory";
import LoadingIndicator from "../loading/loading";
import { focusStore, setFocusStore } from "../../../state";
import { t } from "../../translations/configuration";
import { fixImageUrl } from "../../utils/fixImageUrl";

// Global state for remembering focus position
let savedFocusPosition: { rowIndex: number; itemIndex: number } | null = null;

// LIMITS
const MAX_CATEGORIES = 13; // Maximum number of category rows
const MAX_ITEMS_PER_CATEGORY = 20; // Maximum items per row

// The Movies UI itself, usable either as a full page (see Movies.tsx, the
// /movies route) or embedded inline under Home's header — where selecting
// "Movies" shows this without navigating away and losing the header row.
const MoviesPanel = (props: {
  onEscape: () => void;
  onUp?: () => void;
  // Same imperative-handle pattern as keyboardRef elsewhere in this app —
  // a plain object this component attaches a focusEntry() method to, so a
  // parent (Home) can send focus into this panel's content, e.g. when
  // pressing Down from the header while this panel is open.
  panelRef?: { focusEntry?: () => void };
}) => {
  const params = useParams();
  const navigate = useNavigate();
  // Captured under a name distinct from `props` — CategoryRow below is a
  // nested component with its own `props` parameter that shadows this one.
  const goUpToHeader = () => props.onUp?.();

  const [showLoading, setShowLoading] = createSignal(true);
  const [showMessage, setShowMessage] = createSignal(false);
  const [focusedCategoryIndex, setFocusedCategoryIndex] = createSignal(0);
  const [loadedCategories, setLoadedCategories] = createSignal(new Set<number>());
  const [isReturningFromEntity, setIsReturningFromEntity] = createSignal(false);
  const [showLoadingScreen, setShowLoadingScreen] = createSignal(true);
  const [isNavigating, setIsNavigating] = createSignal(false);

  const [title, setTitle] = createSignal("");
  const [plot, setPlot] = createSignal("");
  const [released, setReleased] = createSignal("");
  const [rating, setRating] = createSignal("");
  const [cast, setCast] = createSignal("");
  const [genre, setGenre] = createSignal("");
  const [duration, setDuration] = createSignal("");
  const [backdrop, setBackdrop] = createSignal("");

  // Pending values for smooth transitions
  const [pendingTitle, setPendingTitle] = createSignal("");
  const [pendingPlot, setPendingPlot] = createSignal("");
  const [pendingReleased, setPendingReleased] = createSignal("");
  const [pendingRating, setPendingRating] = createSignal("");
  const [pendingGenre, setPendingGenre] = createSignal("");
  const [pendingDuration, setPendingDuration] = createSignal("");
  const [pendingBackdrop, setPendingBackdrop] = createSignal("");

  let columnRef: any;
  let loadingScreenRef: any;
  let mainContentRef: any;
  let heroContentRef: any;
  let backdropRef: any;
  let isAnimating = false;

  // Helper function for smooth navigation with fade out
  const handleNavigate = (path: string) => {
    if (isNavigating()) return; // Prevent multiple clicks
    setIsNavigating(true);

    gsap.to(mainContentRef, {
      alpha: 0,
      duration: 0.25,
      ease: "power2.inOut",
      onComplete: () => {
        navigate(path);
        setIsNavigating(false);
      }
    });
  };

  // Animate hero content when it changes
  const animateHeroContent = () => {
    if (!heroContentRef || isAnimating) return;
    isAnimating = true;

    // Fade out
    gsap.to(heroContentRef, {
      alpha: 0,
      duration: 0.2,
      ease: "power2.out",
      onComplete: () => {
        // Update actual content while hidden
        setTitle(pendingTitle());
        setPlot(pendingPlot());
        setRating(pendingRating());
        setReleased(pendingReleased());
        setGenre(pendingGenre());
        setDuration(pendingDuration());

        // Fade in with new content
        gsap.to(heroContentRef, {
          alpha: 1,
          duration: 0.3,
          ease: "power2.in",
          onComplete: () => {
            isAnimating = false;
          }
        });
      }
    });
  };

  // Animate backdrop when it changes
  const animateBackdrop = () => {
    if (!backdropRef) return;

    const newBackdrop = pendingBackdrop();
    const currentBackdrop = backdrop();

    // If there's no current backdrop, just set it immediately
    if (!currentBackdrop && newBackdrop) {
      setBackdrop(newBackdrop);
      gsap.set(backdropRef, { alpha: 1 });
      return;
    }

    // If backdrop hasn't changed, don't animate
    if (newBackdrop === currentBackdrop) return;

    // Animate the transition
    gsap.to(backdropRef, {
      alpha: 0,
      duration: 0.25,
      ease: "power2.out",
      onComplete: () => {
        // Update backdrop while hidden
        setBackdrop(newBackdrop);

        // Fade in with new image
        gsap.to(backdropRef, {
          alpha: 1,
          duration: 0.35,
          ease: "power2.in"
        });
      }
    });
  };

  // Watch for pending content changes
  createEffect(() => {
    const newTitle = pendingTitle();
    const newPlot = pendingPlot();
    const newRating = pendingRating();
    const newReleased = pendingReleased();
    const newGenre = pendingGenre();
    const newDuration = pendingDuration();

    // Only animate if content actually changed
    if (
      newTitle !== title() ||
      newPlot !== plot() ||
      newRating !== rating() ||
      newReleased !== released() ||
      newGenre !== genre() ||
      newDuration !== duration()
    ) {
      animateHeroContent();
    }
  });

  // Watch for backdrop changes
  createEffect(() => {
    const newBackdrop = pendingBackdrop();
    const currentBackdrop = backdrop();

    // Only animate if backdrop actually changed and we have a backdrop ref
    if (newBackdrop && newBackdrop !== currentBackdrop && backdropRef) {
      animateBackdrop();
    } else if (newBackdrop && !currentBackdrop) {
      // First time setting backdrop, no animation needed
      setBackdrop(newBackdrop);
    }
  });

  // Always show loading animation, even when returning from entity
  onMount(() => {
    const fromEntity = sessionStorage.getItem('returningFromEntity');
    if (fromEntity === 'true') {
      setIsReturningFromEntity(true);
      sessionStorage.removeItem('returningFromEntity');
      console.log('🔙 Returning from entity page, will restore focus position');
    } else {
      savedFocusPosition = null;
      console.log('🆕 Fresh entry to movies page');
    }

    // REDUCED loading animation time
    const timeline = gsap.timeline();

    // After 0.4 seconds (reduced from 0.8s), fade out loading screen and fade in content
    timeline.to(loadingScreenRef, {
      alpha: 0,
      duration: 0.3,
      ease: "power2.out",
      delay: 0.4,
      onComplete: () => {
        setShowLoadingScreen(false);
      }
    });

    timeline.to(mainContentRef, {
      alpha: 1,
      duration: 0.4,
      ease: "power2.out"
    }, "-=0.15");

    // Initialize backdrop to full opacity after mount
    setTimeout(() => {
      if (backdropRef) {
        gsap.set(backdropRef, { alpha: 1 });
      }
    }, 100);
  });

  // Same "rating/10 • year • genre • duration" format as ContentBlock's details line
  const heroDetailsLine = createMemo(() => {
    const parts: string[] = [];

    const ratingNum = parseFloat(String(rating()));
    if (!isNaN(ratingNum) && ratingNum > 0) parts.push(`${ratingNum.toFixed(1)}/10`);

    if (released()) parts.push(String(released()));
    if (genre()) parts.push(String(genre()));

    const durationRaw = duration();
    if (durationRaw) {
      const minutes = Number(durationRaw);
      if (!isNaN(minutes) && minutes > 0) {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        parts.push(h > 0 ? `${h}h ${m}m` : `${m}m`);
      } else {
        parts.push(String(durationRaw));
      }
    }

    return parts.join("  •  ");
  });

  const providerCategory = createMemo(() => {
    return createInfiniteScroll(moviesCategory(params.filter || "all"));
  });

  // ✅ LIMIT TO 13 CATEGORIES
  const filteredCategories = createMemo(() => {
    const pages = providerCategory().pages() || [];
    const filtered = pages.filter((cat: any) => cat.categoryID !== "last_added");

    // Limit to MAX_CATEGORIES
    const limited = filtered.slice(0, MAX_CATEGORIES);

    console.log(`📋 Total categories: ${filtered.length}, Limited to: ${limited.length}`);
    return limited;
  });

  // Restore focus position when returning from entity
  createEffect(() => {
    const categories = filteredCategories();

    if (categories.length > 0 && columnRef && isReturningFromEntity() && savedFocusPosition) {
      setTimeout(() => {
        console.log(`🔙 Restoring focus to row ${savedFocusPosition!.rowIndex}, item ${savedFocusPosition!.itemIndex}`);
        setFocusedCategoryIndex(savedFocusPosition!.rowIndex);
      }, 200);
    }
  });

  function CategoryRow(props: any) {
    const { category, index } = props;
    let rowRef: any;

    const getInitialSelectedIndex = () => {
      if (isReturningFromEntity() && savedFocusPosition && savedFocusPosition.rowIndex === index) {
        return savedFocusPosition.itemIndex;
      }
      return 0;
    };

    const [selectedMovieIndex, setSelectedMovieIndex] = createSignal(getInitialSelectedIndex());

    const shouldLoad = createMemo(() => {
      const focused = focusedCategoryIndex();
      const isNearFocus = Math.abs(index - focused) <= 2;
      return isNearFocus || loadedCategories().has(index);
    });

    createEffect(() => {
      if (shouldLoad() && !loadedCategories().has(index)) {
        setLoadedCategories(prev => {
          const newSet = new Set(prev);
          newSet.add(index);
          return newSet;
        });
      }
    });

    const provider = createMemo(() => {
      if (!shouldLoad()) return null;
      return createInfiniteScroll(moviesProvider(params.filter || "all", category.categoryID));
    });

    // ✅ LIMIT TO 20 ITEMS PER CATEGORY
    const moviesForCategory = createMemo(() => {
      if (!provider()) return [];
      const pages = provider().pages();
      const allMovies = pages || [];

      // Limit to MAX_ITEMS_PER_CATEGORY
      const limited = allMovies.slice(0, MAX_ITEMS_PER_CATEGORY);

      console.log(`🎬 Category "${category.category_name}": ${allMovies.length} total, showing ${limited.length}`);
      return limited;
    });

    const handleRowFocus = () => {
      console.log(`🎯 Focus on category row ${index}: ${category.category_name}`);
      setFocusedCategoryIndex(index);

      savedFocusPosition = {
        rowIndex: index,
        itemIndex: selectedMovieIndex()
      };
      console.log(`💾 Saved focus position: row ${index}, item ${selectedMovieIndex()}`);
    };

    const handleSelectedChanged = (newIndex: number) => {
      console.log(`📍 Movie selection changed in row ${index} to movie ${newIndex}`);
      setSelectedMovieIndex(newIndex);

      savedFocusPosition = {
        rowIndex: index,
        itemIndex: newIndex
      };
      console.log(`💾 Updated focus position: row ${index}, item ${newIndex}`);
    };

    onMount(() => {
      console.log(`✅ MOUNTED: CategoryRow ${index} - ${category.category_name}`);
    });

    onCleanup(() => {
      console.log(`❌ UNMOUNTED: CategoryRow ${index} - ${category.category_name}`);
    });

    return (
      <View
        x={80}
        height={365}
        forwardFocus={1}
        marginTop={30}
        onFocus={handleRowFocus}
      >
        <Text skipFocus fontSize={30} color={hexColor("#FFFFFF")} style={{ fontWeight: "bold" }} y={0}>
          {category.category_name}
        </Text>

        <Show
          when={shouldLoad()}
          fallback={
            <View x={400} y={80} skipFocus>
              <Text fontSize={18} color={hexColor("#7d7e82")}>Scroll to load...</Text>
            </View>
          }
        >
          <Show
            when={moviesForCategory().length > 0}
            fallback={
              <View x={400} y={80} skipFocus>
                <LoadingIndicator isCategoriesIndicator={true} />
              </View>
            }
          >
            <View y={50} forwardFocus={0}>
              <Row
                ref={rowRef}
                gap={20}
                scroll="always"
                autofocus={
                  isReturningFromEntity() && savedFocusPosition
                    ? savedFocusPosition.rowIndex === index
                    : index === 0
                }
                selected={selectedMovieIndex()}
                onSelectedChanged={handleSelectedChanged}
              >
                <For each={moviesForCategory()}>
                  {(item: any, i) => (
                    <ItemMovie {...item} index={i()} categoryId={category.categoryID} />
                  )}
                </For>
              </Row>
            </View>
          </Show>
        </Show>
      </View>
    );
  }

  function ItemMovie(props: any) {
    const movieData = props.data;
    const posterSrc = props.src || "/assets/movie-placeholder.png";
    const movieName = movieData?.name || props.shortTitle || "Unknown";
    const streamId = movieData?.stream_id;

    console.log('props', props);


    const handleEnter = () => {
      if (streamId && !isNavigating()) {
        sessionStorage.setItem('returningFromEntity', 'true');
        console.log(`🚀 Navigating to entity from row ${props.categoryId}, item ${props.index}`);

        setFocusStore("moviesDetail", props.index);
        handleNavigate(`/entity/movie/${streamId}/${props.categoryId}`);
      }
    };

    return (
      <View
        style={{
          // Jetstream Standard card: 2:3 poster ratio, subtle radius, 3dp-style focus outline
          width: 260,
          height: 390,
          scale: 1,
          borderRadius: 14,
          border: { width: 0, color: hexColor("#ffffff00") },
          color: hexColor("#00000000"),
          transition: {
            scale: { duration: 250, easing: "ease-in-out" },
            border: { duration: 200 }
          },
          focus: {
            scale: 1.05,
            color: hexColor("#00000000"),
            border: { width: 4, color: hexColor("#ffffff") }
          },
          alignItems: "center",
          justifyContent: "center",
        }}
        onFocus={() => {
          setPendingTitle(movieName);
          setPendingPlot(movieData.plot || "");
          setPendingRating(movieData.rating || "");
          setPendingReleased(movieData.year || "");
          setPendingGenre(movieData.genre || "");
          setPendingDuration(movieData.episode_run_time || "");
          setPendingBackdrop(movieData.stream_icon || "");

          if (movieData.backdrop_path) {
            setPendingBackdrop(movieData.backdrop_path);
          }
        }}
        onEnter={handleEnter}
      >
        <View
          // src={posterSrc}
          src={fixImageUrl(posterSrc)}
          style={{
            width: 252,
            height: 380,
            borderRadius: 14
          }}
          x={4}
          y={5}
        />
      </View>
    );
  }

  createEffect(() => {
    const timeout = setTimeout(() => {
      setShowMessage(filteredCategories().length > 0 ? false : true);
      setShowLoading(false);
    }, 15000);

    onCleanup(() => clearTimeout(timeout));
  });

  const handleEscape = () => {
    if (isNavigating()) return true;

    savedFocusPosition = null;
    sessionStorage.removeItem('returningFromEntity');
    console.log('🚪 Escape pressed - clearing saved focus position');

    setFocusStore("moviesIndex", null);
    setFocusStore("moviesDetail", null);
    setFocusStore("moviesID", null);

    setIsNavigating(true);

    gsap.to(mainContentRef, {
      alpha: 0,
      duration: 0.25,
      ease: "power2.inOut",
      onComplete: () => {
        props.onEscape();
        setIsNavigating(false);
      }
    });

    // Stop this Escape from also bubbling up to an ancestor's onEscape —
    // matters when this panel is embedded inside Home's tree rather than
    // mounted as its own route (see the same fix on SettingsPanel).
    return true;
  };

  return (
    <>
      {/* Loading Screen */}
      <Show when={showLoadingScreen()}>
        <View
          ref={loadingScreenRef}
          width={1920}
          height={1080}
          color={hexColor("#000000")}
          alpha={1}
          style={{ zIndex: 2000, position: "absolute" }}
        >
          <View x={800} y={400}>
            <LoadingIndicator />
          </View>
        </View>
      </Show>

      <View
        width={1920}
        height={1080}
        color={hexColor("#000000")}
        alpha={1}
      />

      {/* Main Content - starts hidden, fades in */}
      <View
        ref={mainContentRef}
        alpha={isReturningFromEntity() ? 1 : 0}
        style={{ position: "absolute", zIndex: 1 }}
      >
        <View style={{ width: 1920, height: 1080, color: hexColor("#000000") }}>
          {/* Black background layer */}
          <View
            style={{
              width: 1920,
              height: 1080,
              color: hexColor("#000000")
            }}
          />

          {/* Image layer on top */}
          <Show when={backdrop()}>
            <View
              ref={backdropRef}
              // src={backdrop()}
              src={fixImageUrl(backdrop())}
              alpha={1}
              style={{
                width: 1230,
                height: 1900,
                objectFit: "cover",
                objectPosition: "center center",
              }}
              x={690}
              y={-550}
            />
          </Show>
        </View>
        <View src={"assets/hero-gradient-mask.png"} onEscape={handleEscape} style={{ width: 1920, height: 1080 }}>

          {/* Hero Content — same layout language as ContentBlock (Entity page):
              title, then a short plot summary, then one dot-separated details line */}
          <View
            x={80}
            y={150}
            ref={heroContentRef}
            alpha={1}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 24,
              width: 1350,
            }}
          >
            <Show when={title()}>
              <Text
                style={{
                  fontSize: 72,
                  fontWeight: 400,
                  lineHeight: 88,
                  color: hexColor("#ffffff"),
                  fontFamily: "Roboto",
                  maxLines: 1,
                  width: 1350,
                  contain: "width",
                  marginBottom: 20,
                }}
              >
                {title()}
              </Text>
            </Show>

            <Show when={plot()}>
              <Text
                contain="width"
                style={{
                  fontSize: 28,
                  lineHeight: 40,
                  width: 800,
                  maxLines: 2,
                  color: hexColor("#B9B9BE"),
                  fontFamily: "Roboto",
                }}
              >
                {plot()}
              </Text>
            </Show>

            <Show when={heroDetailsLine() !== ""}>
              <Text
                style={{
                  fontSize: 28,
                  color: hexColor("#B9B9BE"),
                  fontFamily: "Roboto",
                  maxLines: 1,
                  contain: "width",
                  width: 800,
                }}
              >
                {heroDetailsLine()}
              </Text>
            </Show>
          </View>

          {/* Categories Section */}
          <View y={450} clipping>
            <Show
              when={filteredCategories() && filteredCategories()?.length > 0}
              fallback={
                <View x={750} y={500}>
                  <Text fontSize={30} color={hexColor("#7d7e82")}>
                    {t("noMovies")}
                  </Text>
                </View>
              }
            >
              <View>
                <Column
                  ref={(el: any) => {
                    columnRef = el;
                    if (props.panelRef) props.panelRef.focusEntry = () => columnRef?.setFocus();
                  }}
                  autofocus={true}
                  gap={90}
                  selected={
                    isReturningFromEntity() && savedFocusPosition
                      ? savedFocusPosition.rowIndex
                      : 0
                  }
                  scroll="always"
                  onUp={() => {
                    console.log(`⬆️ Navigating up from column`);
                    goUpToHeader();
                  }}
                  onDown={() => {
                    console.log(`⬇️ Navigating down in column`);
                  }}
                >
                  <Index each={filteredCategories()}>
                    {(category, index) => {
                      console.log(`🔧 Rendering CategoryRow at index ${index}: ${category().category_name}`);
                      return <CategoryRow category={category()} index={index} />;
                    }}
                  </Index>
                </Column>
              </View>
            </Show>
          </View>
        </View>
      </View>
    </>
  );
};

export default MoviesPanel;
